import { Injectable } from '@angular/core';
import europassSampleJs from './europass-sample.js';

@Injectable()
export class Europass {
  private baseRestUrl: string = 'https://europass.cedefop.europa.eu/rest';

  constructor() {
  }

  getLevelFromNumber(numero) {
    switch (numero) {
      case 5:
        return 'experto';
      case 4:
        return 'avanzado';
      case 3:
        return 'medio';
      case 2:
        return 'básico';
      case 1:
        return 'principiante';
    }
    return 'nivel desconocido';
  }

  /**
   * Obtiene un objeto con la estructura del Europass, rellenado con la info del perfil
   * de usuario. El usuario se recibe en el formato Fuifi, en jsonProfile.
   * @param {Object} jsonProfile contiene el perfil del user, tal y como llega de API.
   * @param {Array} professionalExperiences (nullable) Experiencias profesionales previas del usuario.
   * @returns {Object} Objeto con la estructura del Europass JSON, con la info del user.
   */
  async fuifiProfileToJsonEuropass(jsonProfile, professionalExperiences) {

    europassSampleJs.SkillsPassport.LearnerInfo.Identification.PersonName.FirstName =
      jsonProfile.name;
    europassSampleJs.SkillsPassport.LearnerInfo.Identification.PersonName.Surname =
      jsonProfile.lastname;

    // Dirección (Empresa, departamento, centro de trabajo)
    europassSampleJs.SkillsPassport.LearnerInfo.Identification.ContactInfo.Address.
    Contact.AddressLine =
      jsonProfile.team_name;

    europassSampleJs.SkillsPassport.LearnerInfo.Identification.ContactInfo.Address.
    Contact.PostalCode =
      jsonProfile.department;

    europassSampleJs.SkillsPassport.LearnerInfo.Identification.ContactInfo.Address.
    Contact.Municipality =
      jsonProfile.work_center;

    europassSampleJs.SkillsPassport.LearnerInfo.Identification.ContactInfo.Email.Contact =
      jsonProfile.email;

    europassSampleJs.SkillsPassport.LearnerInfo.Headline.Type.Code = 'position';
    europassSampleJs.SkillsPassport.LearnerInfo.Headline.Type.Label = 'Posición';
    europassSampleJs.SkillsPassport.LearnerInfo.Headline.Description.Label =
      jsonProfile.professional_profile;

    // procesar experiencia en proyectos en la organización
    europassSampleJs.SkillsPassport.LearnerInfo.WorkExperience = [];
    jsonProfile.projects.forEach(unProject => {
      const prj = {
        Period: {
          From: {
            Year: unProject.start_date.split('/')[0],
            Month: unProject.start_date.split('/')[1],
          },
          To: {
            Year: unProject.end_date.split('/')[0],
            Month: unProject.end_date.split('/')[1],
          },
          Current: false,
        },
        Position: {
          Label: unProject.rol_project,
        },
        Activities: `Proyecto para: ${unProject.client}\n
        Descripción: ${unProject.project_description}\n
        Tareas realizadas: ${(unProject.tasks) ? unProject.tasks : '-'}`,
        Employer: {
          Name: unProject.name + ' (' + jsonProfile.team_name + ')',
          Sector: {
            Label: '-',
          },
        },
      };
      europassSampleJs.SkillsPassport.LearnerInfo.WorkExperience.push(prj);
    });

    // experiencias profesionales previas (opcional)
    if (professionalExperiences != null && professionalExperiences.length != 0) {
      professionalExperiences.forEach(unaExperiencia => {
        const prj = {
          Period: {
            From: {
              Year: unaExperiencia.date_start.split('-')[0],
              Month: unaExperiencia.date_start.split('-')[1],
            },
            To: null, /* se rellena más adelante */
            Current: true, /* se rellena más adelante */
          },
          Position: {
            Label: unaExperiencia.rol,
          },
          Activities: `Proyecto: ${unaExperiencia.projects}\n
          Skills: ${(unaExperiencia.skills) ? unaExperiencia.skills.map(e => e.name).join(', ') : '-'}\n
          Tareas realizadas: ${(unaExperiencia.performed_functions) ? unaExperiencia.performed_functions : '-'}`,
          Employer: {
            Name: unaExperiencia.company_name,
            Sector: {
              Label: '-',
            },
          },
        };

        if (unaExperiencia.date_end) {
          prj.Period.To = {
            Year: unaExperiencia.date_end.split('-')[0],
            Month: unaExperiencia.date_end.split('-')[1],
          };
          prj.Period.Current = false;
        }

        europassSampleJs.SkillsPassport.LearnerInfo.WorkExperience.push(prj);
      });
    }

    // procesar educación académica
    europassSampleJs.SkillsPassport.LearnerInfo.Education = [];
    jsonProfile.academic_titles.forEach(unTitle => {
      const titulo = {
        Period: {
          From: {
            Year: unTitle.start.split('-')[0],
          },
          To: {
            Year: unTitle.end.split('-')[0],
          },
          Current: false,
        },
        Title: unTitle.name,
        Organisation: {
          Name: unTitle.location,
        },
      };
      europassSampleJs.SkillsPassport.LearnerInfo.Education.push(titulo);
    });

    // procesaar otros cursos de formación
    jsonProfile.training_courses.forEach(unTitle => {
      const titulo = {
        Period: {
          From: {
            Year: unTitle.date.split('-')[0],
          },
          To: {
            Year: unTitle.date.split('-')[0],
          },
          Current: false,
        },
        Title: unTitle.title,
        Organisation: {
          Name: unTitle.location,
        },
      };
      europassSampleJs.SkillsPassport.LearnerInfo.Education.push(titulo);
    });

    europassSampleJs.SkillsPassport.LearnerInfo.Skills = {};
    europassSampleJs.SkillsPassport.LearnerInfo.Skills.Linguistic = {};

    // quitar lengua madre
    europassSampleJs.SkillsPassport.LearnerInfo.Skills.Linguistic.MotherTongue = [];

    // procesar lenguajes
    europassSampleJs.SkillsPassport.LearnerInfo.Skills.Linguistic.ForeignLanguage = [];
    jsonProfile.languages.forEach(unLang => {
      const idioma = {
        Description: {
          Code: unLang.native_name,
          Label: unLang.name,
        },
        ProficiencyLevel: {
          Listening: unLang.level,
          Reading: unLang.level,
          SpokenInteraction: unLang.level,
          SpokenProduction: unLang.level,
          Writing: unLang.level,
        },
      };
      europassSampleJs.SkillsPassport.LearnerInfo.Skills.Linguistic.ForeignLanguage.push(idioma);
    });

    // procesar skills
    europassSampleJs.SkillsPassport.LearnerInfo.Skills.Other = {};
    let strSkills = '';
    jsonProfile.skills.forEach(unSkill => {
      strSkills += `${unSkill.name} (${this.getLevelFromNumber(unSkill.level)}), `;
    });

    europassSampleJs.SkillsPassport.LearnerInfo.Skills.Other = {
      Description: strSkills,
    };

    // Certificaciones
    europassSampleJs.SkillsPassport.LearnerInfo.Achievement = [];
    jsonProfile.certifications.forEach(unCert => {
      const cert = {
        Title: {
          Code: 'certifications',
          Label: 'Certificación',
        },
        Description: `"${unCert.title}", ${unCert.location} (${unCert.date.split('-')[0]})`,
      };
      europassSampleJs.SkillsPassport.LearnerInfo.Achievement.push(cert);
    });

    await this.getBase64ImageFromUrl(jsonProfile.image).then(result => {
      europassSampleJs.SkillsPassport.LearnerInfo.Identification.Photo.Data = result;
    }).catch(error => {
      // console.log('error getBase64ImageFromUrl() -> ' + error);
      europassSampleJs.SkillsPassport.LearnerInfo.Identification.Photo.Data = null;
    });

    return europassSampleJs;
  }

  /**
   * Descarga y convierte una imagen a base64.
   * @param {string} imageUrl Url de la imagen que hay que descargar y convertir a base64.
   * @returns Promesa imagen en base64
   */
  async getBase64ImageFromUrl(imageUrl) {
    const res = await fetch(imageUrl);
    const blob = await res.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // console.log('loaded image base64');
        const strResult = reader.result.toString();
        const ret =
          strResult.replace(/^data:((image\/(jpeg|png|jpg))|(application\/json));base64,/, '');
        return resolve(ret);
      };

      reader.onerror = () => {
        // console.log('invalid image base64');
        return reject(this);
      };
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Solo para versión WEB; no para apps.
   * Envía el JSON a la API de europass y descarga el PDF resultante.
   * Si éxito, abre el diálogo para guardar el PDF del CV.
   *
   * @param {Object} jsonEuropass
   * @returns Promise. Si éxito, abre el diálogo para guardar el PDF del CV.
   */
  cvJsonToPdfWithXMLHttpRequest(jsonEuropass, langCV) {

    return new Promise((resolve, reject) => {
      const path = '/v1/document/to/pdf-cv';

      let namePerson = ''; // name persona, se usa para nombre del pdf.
      try {
        namePerson = '-' + jsonEuropass.SkillsPassport.LearnerInfo.Identification.PersonName.FirstName;
      } catch (e) {}

      const url = this.baseRestUrl + path;
      const ticks = new Date().getTime();
      const filename = `europass${namePerson}-${ticks}.pdf`;
      const xmlhttp = new XMLHttpRequest();
      xmlhttp.open('POST', url, true);

      xmlhttp.responseType = 'blob';
      xmlhttp.onreadystatechange = function () {

        if (xmlhttp.readyState == 4 && xmlhttp.status == 200) {

          const blob =
            new Blob([this.response], { type: xmlhttp.getResponseHeader('Content-Type') });

          if (typeof window.navigator.msSaveBlob !== 'undefined') {
            window.navigator.msSaveBlob(blob, filename);
            return resolve(true);
          } else {
            const URL = window.URL;
            const downloadUrl = URL.createObjectURL(blob);
            // This way it downloads the file in the 3 major browsers with file name:
            const a = document.createElement('a');
            // safari doesn't support this yet
            if (typeof a.download === 'undefined') {
              window.location.assign(downloadUrl);
            } else {
              a.href = downloadUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
            }
            setTimeout(() => {
              URL.revokeObjectURL(downloadUrl);
            },         100); // cleanup 100

            return resolve(downloadUrl);
          }
        } else if (xmlhttp.readyState == 4 && xmlhttp.status != 200) {
          return reject(xmlhttp.status); // si http code distinto de 200, error.
        }
      };

      xmlhttp.setRequestHeader('Content-Type', 'application/json');
      xmlhttp.setRequestHeader('Accept-Language', langCV);
      xmlhttp.send(JSON.stringify(jsonEuropass));
    });
  }
}



// WEBPACK FOOTER //
// ./src/services/europass.ts